import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import toast from 'react-hot-toast';
import { privateRequest } from 'services/privateRequest';

// Async thunk to fetch work types
export const getWorkTypes = createAsyncThunk(
  'relatedWork/getWorkTypes',
  async ({ pageNo = 1, pageSize = 10 } = {}) => {
    const response = await privateRequest.get('api/release/work-type', {
      params: { pageNo, pageSize }
    });
    return response.data;
  }
);

export const addRelatedWork = createAsyncThunk(
  'relatedWork/addRelatedWork',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await privateRequest.post('api/release/work-item', payload);
      toast.success(response.data?.message || 'Related work added successfully');
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add related work');
      return rejectWithValue(error.response?.data || { message: 'Failed to add related work' });
    }
  }
);

export const updateRelatedWorkItem = createAsyncThunk(
  'relatedWork/updateItem',
  async ({ releaseWorkItemId, data }, { rejectWithValue }) => {
    try {
      const response = await privateRequest.put(`api/release/work-item/${releaseWorkItemId}`, data);
      toast.success(response.data?.message || 'Related work updated successfully');
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update related work');
      return rejectWithValue(error.response?.data || { message: 'Failed to update related work' });
    }
  }
);

export const deleteRelatedWorkItem = createAsyncThunk(
  'relatedWork/deleteItem',
  async (releaseWorkItemId, { rejectWithValue }) => {
    try {
      const response = await privateRequest.delete(`api/release/work-item/${releaseWorkItemId}`);
      toast.success(response.data?.message || 'Related work deleted successfully');
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete related work');
      return rejectWithValue(error.response?.data || { message: 'Failed to delete related work' });
    }
  }
);

export const getRelatedWorkItems = createAsyncThunk(
  'relatedWork/getRelatedWorkItems',
  async (releaseId, { rejectWithValue }) => {
    try {
      const response = await privateRequest.get(`api/release/${releaseId}/work-items`);
      return response.data;
    } catch (error) {
    
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch related work items' });
    }
  }
);

export const linkIssuesToWorkItem = createAsyncThunk(
  "release/linkIssuesToWorkItem",
  async ({ releaseWorkItemId, payload }, { rejectWithValue }) => {
    try {
      const response = await privateRequest.put(
        `api/release/work-item/${releaseWorkItemId}`,
        payload
      );
      toast.success("Issues linked successfully");
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to link issues");
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  }
);

const relatedWorkSlice = createSlice({
  name: 'relatedWork',
  initialState: {
    workTypes: [],
    relatedWorkItems: [],
    linkedWorkItems: [],
    loading: false,
    error: null,
    totalWorkTypes: 0
  },
  reducers: {
    clearWorkTypes: (state) => {
      state.workTypes = [];
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getWorkTypes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getWorkTypes.fulfilled, (state, action) => {
        state.loading = false;
        state.workTypes = action.payload.data?.data || [];
        state.totalWorkTypes = action.payload.data?.count?.total || 0;
      })
      .addCase(getWorkTypes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(addRelatedWork.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addRelatedWork.fulfilled, (state, action) => {
        state.loading = false;
        const newItem = action.payload.data || action.payload;
        state.relatedWorkItems.push(newItem);
      })

      .addCase(addRelatedWork.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to add related work';
      })
      .addCase(getRelatedWorkItems.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getRelatedWorkItems.fulfilled, (state, action) => {
        state.loading = false;
        state.relatedWorkItems = action.payload.data?.data || [];
      })
      .addCase(getRelatedWorkItems.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to fetch related work items';
      })
      .addCase(deleteRelatedWorkItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteRelatedWorkItem.fulfilled, (state, action) => {
        state.loading = false;
        state.relatedWorkItems = state.relatedWorkItems.filter(
          item => item.id !== action.meta.arg
        );
      })
      .addCase(deleteRelatedWorkItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to delete related work';
      })
      .addCase(linkIssuesToWorkItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(linkIssuesToWorkItem.fulfilled, (state, action) => {
        state.loading = false;
        state.linkedWorkItems.push(action.payload);
      })
      .addCase(linkIssuesToWorkItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearWorkTypes } = relatedWorkSlice.actions;
export default relatedWorkSlice.reducer;